import React from "react";

import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import PageMeta from "Components/PageMeta";
import Button from "ds/Button";
import Loading from "ds/PageLoading";
import { capitalize } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { useGetServiceAppBlob } from "Reducers/environment/git/hooks/useGetServiceAppBlob";
import FilePreviewer from "src/organization/common/components/FilePreviewer";
import { useAppSelector } from "Store/hooks";

import EmptyCard from "../components/EmptyCard";

import { RoutesDescription } from "./components/RoutesDescription";
import * as S from "./ServiceConfiguration.styles";

const ServiceConfiguration = () => {
  const { formatMessage } = useIntl();
  const {
    appName,
    projectId = "",
    environmentId = "",
    organizationId = ""
  } = useParams<{
    appName: string;
    projectId: string;
    environmentId: string;
    organizationId: string;
  }>();

  const environment = useAppSelector(state =>
    environmentSelector(state, {
      projectId,
      organizationId,
      environmentId
    })
  );

  const { data: blob, isLoading } = useGetServiceAppBlob({
    appName,
    environment
  });

  const { data, currentServiceFilePath } = blob;

  return (
    <S.ServiceConfigurationRoot>
      <PageMeta title={capitalize(formatMessage({ id: "services" }))} />
      {appName === "routes" && <RoutesDescription />}
      {isLoading && <Loading iconOnly />}
      {!isLoading && !data && (
        <EmptyCard>
          {formatMessage<React.ReactNode>(
            { id: "services.noconfiguration" },
            {
              code: chunks => <S.Code variant="inline">{chunks}</S.Code>,
              appConfigFile: formatMessage({ id: "app_config_file" }),
              appApplicationFilePath: formatMessage({
                id: "app_applications_file_path"
              })
            }
          )}
          <br />
          <Button
            analyticId="learnmore"
            variant="link"
            target="blank"
            href={formatMessage({ id: "links.documentation.app" })}
          >
            {formatMessage({ id: "learnmore" })}
          </Button>
        </EmptyCard>
      )}
      {data && (
        <FilePreviewer
          filename={
            appName === "app" ? "applications.yaml" : currentServiceFilePath
          }
          content={data}
        />
      )}
    </S.ServiceConfigurationRoot>
  );
};

export default ServiceConfiguration;
